// -----------------------------------------------------------------------------
// 취약점 진단 상세결과 보고서(엑셀) 생성 모듈
//
// public/ 의 템플릿 "sample_취약점_진단_상세결과보고서.xlsm" 를 exceljs로 읽어
// 표지/로고/셀서식/병합/수식/인쇄설정을 그대로 유지한 채, AnalysisResultPop.js 가
// 사용하는 API 데이터만 채워 다운로드한다.
//
// ※ 템플릿이 .xlsm(매크로 포함)이면 출력물에도 VBA 매크로를 보존한다.
//   exceljs 는 VBA(vbaProject.bin)를 읽지/쓰지 못하므로, 작성 후 JSZip 단계에서
//   원본 템플릿의 vbaProject.bin 을 직접 끼워 넣어 .xlsm 으로 다운로드한다(postProcessXlsx).
//
// 템플릿 구조
//   - "표지"            : 회사명/날짜/진단타입 토큰을 실제 값으로 치환
//   - "자산결과 통합본" : 노출 상태. 다운로드 후 매크로(4개 모듈)로 전체 진단데이터를
//                         모아 정렬/진단 후 각 DB 시트로 재적용하는 작업 시트 → 그대로 둠
//   - "자산목록"        : 자산 메타 + 기밀/무결/가용 점수 (DB 시트가 VLOOKUP)
//   - "위협"            : 숨김 위협 참조표 → 그대로 둠
//   - "DB001/002/..."   : 자산 1개당 1시트. 헤더는 '자산목록'을 VLOOKUP 하는 수식이라
//                         시트 이름 = 자산코드 로 만들고 진단항목 행(19~)만 채우면 됨.
//
// ※ exceljs 의 worksheet.model 복제는 "셀 병합"을 복원하지 못하는 경우가 있어,
//   복제 후 병합을 직접 다시 적용한다(정적 병합 + 진단항목 수에 따른 동적 병합).
// -----------------------------------------------------------------------------
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import axios from '../Common/axios';
import {
    getFindAnalysisResultList,
    getFindInfoAssetListData,
} from '../Common/api';

// public 루트의 템플릿 파일명 (.xlsm = 매크로 포함. 매크로가 없으면 일반 .xlsx 로 다운로드됨)
const TEMPLATE_FILE = 'sample_취약점_진단_상세결과보고서.xlsm';

const SHEET = { asset: '자산목록' };

// 진단항목 표: 첫 행(19행). 템플릿에 그려진 진단항목 행 수는 템플릿에서 자동 감지(아래 detectMeasureRows).
// 요약("보안 수준") 3행은 진단항목 바로 아래에 위치.
const MEASURE_START_ROW = 19;
const DEFAULT_MEASURE_ROWS = 24; // 감지 실패 시 기본값
// '자산목록' 데이터 시작 행
const ASSET_START_ROW = 4;

// DB 상세시트의 "정적" 병합(1~18행: 진단항목 수와 무관하게 고정)
const DB_STATIC_MERGES = [
    'M1:N4', 'A4:B4', 'C4:E4', 'F4:G4', 'H4:I4',
    'A5:B5', 'C5:E5', 'F5:G5', 'H5:I5',
    'A6:B6', 'C6:E6', 'F6:G6', 'H6:I6',
    'A7:B7', 'C7:E7', 'F7:G7', 'H7:I7',
    'F8:G8', 'H8:I8',
    'A10:B10', 'C10:D10', 'E10:F10', 'G10:H10',
    'A11:B11', 'C11:D11', 'E11:F11', 'G11:H11',
    'A18:B18', 'C18:G18',
];

// 템플릿의 진단항목 행 수를 자동 감지 → 템플릿을 120행 등으로 늘려도 코드 수정 불필요.
//  1) 분류(A) 세로 병합 'A19:A{end}' 의 끝 행으로 산출 (예: A19:A42→24, A19:A138→120)
//  2) (보조) '보안 수준' 요약 라벨이 있는 행 = 진단항목 바로 아래 → 그 위까지가 진단항목
function detectMeasureRows(ws) {
    const merges = (ws.model && ws.model.merges) || [];
    if (Array.isArray(merges)) {
        for (const rng of merges) {
            const m = String(rng).match(/^A19:A(\d+)$/);
            if (m) return Math.max(1, parseInt(m[1], 10) - 18);
        }
    }
    for (let r = 20; r <= 19 + 1000; r++) {
        const v = ws.getCell(`A${r}`).value;
        if (v && String(v).includes('보안 수준')) return Math.max(1, r - 19);
    }
    return DEFAULT_MEASURE_ROWS;
}

// 템플릿 전체 병합 목록(진단항목 행 수 기준) - 병합 보장/복원에 사용
function templateAllMerges(measureRows) {
    const last = 18 + measureRows;          // 마지막 진단항목 행
    const merges = [...DB_STATIC_MERGES, `A19:A${last}`];
    for (let r = 19; r <= last; r++) merges.push(`C${r}:G${r}`);
    const s = last + 1;                     // 요약 시작 행
    merges.push(`A${s}:B${s + 2}`, `C${s}:G${s}`, `C${s + 1}:G${s + 1}`, `C${s + 2}:G${s + 2}`);
    return merges;
}

// 결과 코드 정규화: API(Y/N/SELF/P/NA) → 엑셀 표기(Y/N/P/N/A/SELF)
function normalizeResult(code) {
    if (code == null) return '';
    const c = String(code).trim().toUpperCase();
    if (c === 'NA' || c === 'N/A') return 'N/A';
    return c;
}

// 진단항목 중요도 점수(H열) = API total_score. 결과(N/A 포함)와 무관하게 모든 항목에 표기.
// (이전에는 위험도 risk_score 가 잘못 들어갔음)
function importanceValue(m) {
    return numOrBlank(m.total_score);
}

function numOrBlank(v) {
    if (v === '' || v == null) return '';
    const n = Number(v);
    return Number.isFinite(n) ? n : v;
}

// 엑셀 시트 이름 규칙(31자, 특수문자 제거)
function safeSheetName(name, fallback) {
    let n = String(name || fallback || 'SHEET').replace(/[\\/?*[\]:]/g, '_').trim();
    if (!n) n = fallback || 'SHEET';
    return n.slice(0, 31);
}

// axios 응답을 항상 배열로 정규화
function toArray(resData) {
    if (Array.isArray(resData)) return resData;
    if (resData && Array.isArray(resData.data)) return resData.data;
    return [];
}

function setAssetGradeFormulas(ws, r) {
    ws.getCell(`N${r}`).value = {
        formula: `IF(SUM(K${r}:M${r})>=7,"가",IF(SUM(K${r}:M${r})>=5,"나",IF(SUM(K${r}:M${r})>=1,"다","")))`,
    };
    ws.getCell(`O${r}`).value = {
        formula: `IF(SUM(K${r}:M${r})>=7,"3",IF(SUM(K${r}:M${r})>=5,"2",IF(SUM(K${r}:M${r})>=1,"1")))`,
    };
}

/**
 * 상세결과 보고서 엑셀 생성 및 다운로드
 * @param {object} opts
 * @param {string} opts.reportId    보고서ID (필수)
 * @param {string} opts.resultType  결과구분 C/U (필수)
 * @param {string} [opts.reportName] 파일명에 사용할 보고서명
 * @param {string} [opts.companyName] 표지 com_cd(회사명) 치환값
 * @param {string} [opts.diagType]   표지 asset_type(진단타입) 치환값
 * @param {string} [opts.diagDate]   표지 year.month(날짜, YYYY.MM) 치환값 - 텍스트박스
 */
export async function exportDbDetailReport({ reportId, resultType, reportName, companyName, diagDate, diagType }) {
    if (!reportId || !resultType) {
        throw new Error('reportId, resultType 이 필요합니다.');
    }

    // 1) 데이터 조회 ----------------------------------------------------------
    const [resultRes, assetRes] = await Promise.all([
        axios.get(getFindAnalysisResultList(reportId, resultType, '', '', '', 1, 999999999)),
        axios.get(getFindInfoAssetListData(reportId)),
    ]);

    const resultRows = toArray(resultRes.data && resultRes.data.data ? resultRes.data.data : resultRes.data)
        .filter((r) => r && r.measure_nm != null);
    const assetRows = toArray(assetRes.data);

    if (resultRows.length === 0) {
        throw new Error('진단결과 데이터가 없습니다. 먼저 결과를 등록/분석하세요.');
    }

    const assetMetaByCd = {};
    assetRows.forEach((a) => { if (a && a.asset_cd) assetMetaByCd[a.asset_cd] = a; });

    // 자산코드별 진단항목 그룹핑(등장 순서 유지)
    const measuresByAsset = {};
    const assetOrder = [];
    resultRows.forEach((row) => {
        const cd = row.asset_cd;
        if (!cd) return;
        if (!measuresByAsset[cd]) { measuresByAsset[cd] = []; assetOrder.push(cd); }
        measuresByAsset[cd].push(row);
    });
    assetOrder.forEach((cd) => {
        measuresByAsset[cd].sort((a, b) => String(a.measure_cd).localeCompare(String(b.measure_cd)));
    });

    const assets = assetOrder.map((cd) => {
        const meta = assetMetaByCd[cd] || {};
        const first = measuresByAsset[cd][0] || {};
        return {
            asset_cd: cd,
            asset_nm: meta.asset_nm || first.asset_nm || '',
            asset_ip: meta.asset_ip || first.asset_ip || '',
            asset_spec: meta.asset_spec || '',
            os_ver: meta.os_ver || '',
            service_nm: meta.service_nm || '',
            location_nm: meta.location_nm || '',
            manager_nm: meta.manager_nm || '',
            supervisor_nm: meta.supervisor_nm || '',
            mac_no: meta.mac_no || '',
            confidential_score: meta.confidential_score,
            integrity_score: meta.integrity_score,
            availability_score: meta.availability_score,
            sheetName: safeSheetName(cd, 'ASSET'),
        };
    });

    // 2) 템플릿 로드 ----------------------------------------------------------
    const templateUrl = `${process.env.PUBLIC_URL || ''}/${encodeURI(TEMPLATE_FILE)}`;
    const resp = await fetch(templateUrl);
    if (!resp.ok) throw new Error(`템플릿 파일을 불러올 수 없습니다: ${TEMPLATE_FILE}`);
    // 원본 바이트는 매크로(vbaProject.bin) 추출을 위해 보관(exceljs 는 VBA 를 유실시킴)
    const templateArrayBuffer = await resp.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(templateArrayBuffer);

    // 3) 템플릿 DB 상세시트 확보 -------------------------------------------
    //    제거 후 model 복제 시 행삭제(spliceRows)가 동작하지 않으므로, 기존 시트를
    //    "이름만 바꿔 재사용"한다(네이티브 시트라 행삭제/병합이 정상 동작).
    //    자산 수가 템플릿 시트 수보다 많을 때만 model 복제로 추가한다.
    const tplDbSheets = wb.worksheets
        .filter((ws) => /^DB\d+$/i.test(ws.name))
        .sort((a, b) => a.name.localeCompare(b.name));
    if (tplDbSheets.length === 0) throw new Error('템플릿에서 DB 상세시트(DB001 등)를 찾을 수 없습니다.');
    // 템플릿에 그려진 진단항목 행 수 자동 감지 (24/120 등)
    const measureRows = detectMeasureRows(tplDbSheets[0]);
    const dbModelJson = JSON.stringify(tplDbSheets[0].model);
    // 자산코드(최종 시트명)와 충돌하지 않도록 임시 이름 부여
    tplDbSheets.forEach((ws, i) => { ws.name = `__TPL_DB_${i}__`; });

    // 4) '자산목록' 채우기 (샘플 자산행 완전 제거) ---------------------------
    const assetWs = wb.getWorksheet(SHEET.asset);
    if (assetWs) {
        // 4행(스타일 템플릿)만 남기고 그 아래 모든 행 제거 → 샘플 자산 삭제
        if (assetWs.rowCount > ASSET_START_ROW) {
            assetWs.spliceRows(ASSET_START_ROW + 1, assetWs.rowCount - ASSET_START_ROW);
        }
        if (assets.length > 1) {
            assetWs.duplicateRow(ASSET_START_ROW, assets.length - 1, true);
        }
        assets.forEach((a, idx) => {
            const r = ASSET_START_ROW + idx;
            assetWs.getCell(`A${r}`).value = a.asset_cd;
            assetWs.getCell(`B${r}`).value = a.asset_nm;
            assetWs.getCell(`C${r}`).value = a.asset_ip;
            assetWs.getCell(`D${r}`).value = a.asset_spec;
            assetWs.getCell(`E${r}`).value = a.os_ver;
            assetWs.getCell(`F${r}`).value = '-';
            assetWs.getCell(`G${r}`).value = a.service_nm;
            assetWs.getCell(`H${r}`).value = a.location_nm;
            assetWs.getCell(`I${r}`).value = a.manager_nm;
            assetWs.getCell(`J${r}`).value = a.supervisor_nm;
            assetWs.getCell(`K${r}`).value = numOrBlank(a.confidential_score);
            assetWs.getCell(`L${r}`).value = numOrBlank(a.integrity_score);
            assetWs.getCell(`M${r}`).value = numOrBlank(a.availability_score);
            setAssetGradeFormulas(assetWs, r);
            assetWs.getCell(`P${r}`).value = a.mac_no;
        });
        // 안전장치: 자산 수보다 많은 잔여행 제거
        const lastAssetRow = ASSET_START_ROW + assets.length - 1;
        if (assetWs.rowCount > lastAssetRow) {
            assetWs.spliceRows(lastAssetRow + 1, assetWs.rowCount - lastAssetRow);
        }
        // 인쇄영역을 실제 데이터 범위로 설정
        assetWs.pageSetup = { ...(assetWs.pageSetup || {}), printArea: `A1:O${lastAssetRow}` };
    }

    // 5) 자산별 DB 상세시트 생성 --------------------------------------------
    const allTplMerges = templateAllMerges(measureRows);
    assets.forEach((a, i) => {
        let ws;
        if (i < tplDbSheets.length) {
            // 템플릿 시트 재사용(네이티브 → 행삭제/병합 정상)
            ws = tplDbSheets[i];
            ws.name = a.sheetName;
        } else {
            // 부족분은 model 복제로 추가(행삭제 제약 있음)
            ws = wb.addWorksheet(a.sheetName);
            const model = JSON.parse(dbModelJson);
            model.name = a.sheetName;
            model.id = ws.id;
            ws.model = model;
            ws.name = a.sheetName;
        }
        fillDbSheet(ws, measuresByAsset[a.asset_cd] || [], allTplMerges, measureRows);
    });
    // 사용하지 않은 템플릿 DB 시트 제거
    tplDbSheets.slice(assets.length).forEach((ws) => wb.removeWorksheet(ws.id));

    // 6) 표지 셀 토큰 치환(com_cd→회사명, asset_type→진단타입) ----------------
    //    (날짜 year.month 는 텍스트박스라 exceljs로 처리 불가 → 7)에서 zip 후처리)
    try {
        const coverWs = wb.worksheets[0];
        const tokens = [
            { re: /com_cd/g, value: companyName },
            { re: /asset_type/g, value: diagType },
        ].filter((t) => t.value);
        if (coverWs && tokens.length) {
            coverWs.eachRow({ includeEmpty: false }, (row) => {
                row.eachCell({ includeEmpty: false }, (cell) => replaceCellTokens(cell, tokens));
            });
        }
    } catch (e) {
        // 표지 치환 실패는 무시(데이터 시트가 핵심)
        // eslint-disable-next-line no-console
        console.warn('표지 토큰 치환 건너뜀:', e);
    }

    // 7) 엑셀 작성 → zip 후처리(명명범위 정리 + 표지 날짜 + 매크로 주입) → 다운로드 --------
    const buffer = await wb.xlsx.writeBuffer();
    const { data: finalBuffer, hasMacro } = await postProcessXlsx(buffer, diagDate, templateArrayBuffer);
    // 매크로가 주입되면 .xlsm(매크로 사용 통합문서)로, 아니면 일반 .xlsx 로 저장
    const ext = hasMacro ? 'xlsm' : 'xlsx';
    const blob = new Blob([finalBuffer], {
        type: hasMacro
            ? 'application/vnd.ms-excel.sheet.macroEnabled.12'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const baseName = (reportName || reportId || 'report').replace(/[\\/?*[\]:]/g, '_');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}_취약점 진단 상세결과보고서.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// DB 상세시트 채우기: 샘플값 제거 → 병합 보장 → 값 채우기 → 남는 행 숨김
// ※ 행 위치는 그대로 두고(템플릿 고정 양식), 진단항목이 적으면 남는 행을 "숨김"으로 처리.
//   exceljs 의 spliceRows(행 삭제)가 이 환경에서 동작하지 않아(값만 지워지고 테두리 잔존)
//   숨김 방식으로 빈 표/테두리를 확실히 제거한다.
//   ※ 진단항목 수(measureRows)는 템플릿에서 자동 감지된 값. 데이터 항목이 이를 초과하면
//     초과분은 요약행을 침범하므로, 템플릿 행 수 ≥ 최대 진단항목 수 로 유지해야 한다.
function fillDbSheet(ws, measures, allTplMerges, measureRows) {
    const n = measures.length || 1;
    const lastMeasureRow = MEASURE_START_ROW + measureRows;   // 템플릿 마지막 진단행 +1 (반복 경계)
    const summaryLastRow = MEASURE_START_ROW + measureRows + 2; // 요약 3행 마지막 (= 21 + measureRows - 1)

    if (n > measureRows) {
        // 템플릿 행 수보다 데이터가 많음 → 샘플엑셀의 진단항목 행을 더 늘려야 함
        // eslint-disable-next-line no-console
        console.warn(`진단항목(${n})이 템플릿 행 수(${measureRows})를 초과합니다. 샘플엑셀의 진단항목 행을 늘려주세요.`);
    }

    // (1) 템플릿 진단항목 행 샘플값 전부 제거
    for (let r = MEASURE_START_ROW; r < lastMeasureRow; r++) {
        ['B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'].forEach((col) => {
            ws.getCell(`${col}${r}`).value = null;
        });
    }

    // (2) 병합 보장 (네이티브 시트는 이미 있음→무시 / model 복제본은 추가)
    allTplMerges.forEach((range) => { try { ws.mergeCells(range); } catch (e) { /* already merged */ } });

    // (3) 4~7행(자산정보) 우측 테두리 복원
    for (let r = 4; r <= 7; r++) {
        const cell = ws.getCell(`I${r}`);
        cell.border = { ...(cell.border || {}), right: { style: 'thin', color: { argb: 'FF000000' } } };
    }

    // (4) 진단항목 값 채우기 (19 ~ 18+n), 해당 행은 보이게
    measures.forEach((m, idx) => {
        const r = MEASURE_START_ROW + idx;
        ws.getRow(r).hidden = false;
        ws.getCell(`B${r}`).value = m.measure_cd || '';
        ws.getCell(`C${r}`).value = m.measure_nm || '';
        ws.getCell(`H${r}`).value = importanceValue(m);
        ws.getCell(`I${r}`).value = normalizeResult(m.result_cd);
        ws.getCell(`L${r}`).value = m.result_desc || '';
    });

    // (5) 남는 진단항목 행(19+n ~ 마지막 진단행) 숨김 → 빈 표/테두리 제거 (요약은 그대로 유지)
    for (let r = MEASURE_START_ROW + n; r < lastMeasureRow; r++) {
        ws.getRow(r).hidden = true;
    }

    // (6) 인쇄영역 (요약 포함; 숨긴 행은 자동 collapse 되어 빈칸 없이 출력)
    ws.pageSetup = { ...(ws.pageSetup || {}), printArea: `A1:N${summaryLastRow}` };
}

// 셀 값(문자열/리치텍스트)의 토큰을 실제 값으로 치환
function replaceCellTokens(cell, tokens) {
    const apply = (text) => tokens.reduce((acc, t) => acc.replace(t.re, t.value), text);
    const v = cell.value;
    if (typeof v === 'string') {
        const next = apply(v);
        if (next !== v) cell.value = next;
    } else if (v && typeof v === 'object' && Array.isArray(v.richText)) {
        let changed = false;
        const richText = v.richText.map((seg) => {
            if (!seg.text) return seg;
            const next = apply(seg.text);
            if (next !== seg.text) { changed = true; return { ...seg, text: next }; }
            return seg;
        });
        if (changed) cell.value = { richText };
    }
}

// 작성된 xlsx(zip)를 직접 열어 두 가지를 후처리한다.
//  (1) workbook.xml 에서 깨진 통합문서 명명범위(_xlnm.* 이외 = 외부참조 드롭다운 소스 등) 제거
//      → Excel "내용 복구(명명된 범위 제거)" 경고 방지
//  (2) 표지 날짜: drawing 에 "year_month" 플레이스홀더가 있으면 치환,
//      없으면(exceljs 가 텍스트박스를 보존하지 못해 유실된 경우) 로고 도형에 날짜 텍스트박스를 추가
async function postProcessXlsx(buffer, dateText, templateArrayBuffer) {
    let hasMacro = false;
    try {
        const zip = await JSZip.loadAsync(buffer);
        let changed = false;

        // (1) workbook.xml 명명범위 정리
        const wbFile = zip.file('xl/workbook.xml');
        if (wbFile) {
            const wbXml = await wbFile.async('string');
            const cleaned = wbXml.replace(/<definedName\b[^>]*>[\s\S]*?<\/definedName>/g, (tag) => {
                const m = tag.match(/name="([^"]*)"/);
                const nm = m ? m[1] : '';
                // 인쇄영역/자동필터(_xlnm.*)만 유지, 나머지(외부참조 명명범위)는 제거
                return nm.startsWith('_xlnm.') ? tag : '';
            });
            if (cleaned !== wbXml) { zip.file('xl/workbook.xml', cleaned); changed = true; }
        }

        // (2) 표지 날짜
        if (dateText) {
            const placeholder = /year[._-]?month/gi;
            const drawingPaths = Object.keys(zip.files)
                .filter((p) => /^xl\/drawings\/.*\.xml$/i.test(p));

            let dateDone = false;
            // 2-a) 플레이스홀더가 보존돼 있으면 치환
            for (const p of drawingPaths) {
                const xml = await zip.file(p).async('string');
                if (/year[._-]?month/i.test(xml)) {
                    zip.file(p, xml.replace(placeholder, () => escapeXml(dateText)));
                    dateDone = true;
                    changed = true;
                }
            }
            // 2-b) 플레이스홀더 유실 시: 로고(이미지) 도형에 날짜 텍스트박스 추가
            if (!dateDone) {
                for (const p of drawingPaths) {
                    let xml = await zip.file(p).async('string');
                    if ((/<xdr:pic\b/i.test(xml) || /<a:blip\b/i.test(xml)) && xml.includes('</xdr:wsDr>')) {
                        zip.file(p, xml.replace('</xdr:wsDr>', coverDateAnchor(dateText) + '</xdr:wsDr>'));
                        changed = true;
                        break;
                    }
                }
            }
        }

        // (3) 매크로(VBA) 주입: 템플릿(.xlsm)에 vbaProject.bin 이 있으면 출력물에 복사.
        //     exceljs 는 VBA 를 읽지/쓰지 못해 유실되므로 zip 단계에서 직접 끼워 넣는다.
        if (templateArrayBuffer) {
            try {
                const tplZip = await JSZip.loadAsync(templateArrayBuffer);
                const vbaFile = tplZip.file('xl/vbaProject.bin');
                if (vbaFile) {
                    await injectMacro(zip, await vbaFile.async('uint8array'));
                    hasMacro = true;
                    changed = true;
                }
            } catch (e) {
                // 매크로 주입 실패 시 일반 xlsx 로 다운로드(데이터는 정상)
                // eslint-disable-next-line no-console
                console.warn('매크로(VBA) 주입 건너뜀:', e);
            }
        }

        if (!changed) return { data: buffer, hasMacro };
        return { data: await zip.generateAsync({ type: 'arraybuffer' }), hasMacro };
    } catch (e) {
        // 후처리 실패 시 원본 그대로 사용
        // eslint-disable-next-line no-console
        console.warn('xlsx 후처리 건너뜀:', e);
        return { data: buffer, hasMacro };
    }
}

// 출력 zip 에 VBA 프로젝트를 주입해 "매크로 사용 통합문서(.xlsm)"로 만든다.
//  1) xl/vbaProject.bin 추가
//  2) [Content_Types].xml: 워크북 메인파트를 macroEnabled 타입으로 바꾸고 vbaProject 타입 등록
//  3) xl/_rels/workbook.xml.rels: vbaProject 관계 추가
// ※ 표준 모듈(Module) 매크로는 시트 codeName 바인딩이 필요 없어 이 주입만으로 동작한다.
async function injectMacro(zip, vbaBin) {
    // 1) VBA 바이너리 추가
    zip.file('xl/vbaProject.bin', vbaBin);

    // 2) 콘텐츠 타입: 워크북 메인파트 → macroEnabled, vbaProject 파트 타입 등록
    const ctFile = zip.file('[Content_Types].xml');
    if (ctFile) {
        let ct = await ctFile.async('string');
        ct = ct.replace(
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml',
            'application/vnd.ms-excel.sheet.macroEnabled.main+xml',
        );
        if (!/vbaProject\.bin/i.test(ct)) {
            ct = ct.replace(
                '</Types>',
                '<Override PartName="/xl/vbaProject.bin" ContentType="application/vnd.ms-office.vbaProject"/></Types>',
            );
        }
        zip.file('[Content_Types].xml', ct);
    }

    // 3) 워크북 관계: vbaProject 관계 추가(충돌하지 않는 rId 생성)
    const relsPath = 'xl/_rels/workbook.xml.rels';
    const relsFile = zip.file(relsPath);
    if (relsFile) {
        let rels = await relsFile.async('string');
        if (!/vbaProject\.bin/i.test(rels)) {
            const ids = [...rels.matchAll(/Id="rId(\d+)"/g)].map((m) => parseInt(m[1], 10));
            const nextId = (ids.length ? Math.max(...ids) : 0) + 1;
            rels = rels.replace(
                '</Relationships>',
                `<Relationship Id="rId${nextId}" Type="http://schemas.microsoft.com/office/2006/relationships/vbaProject" Target="vbaProject.bin"/></Relationships>`,
            );
        }
        zip.file(relsPath, rels);
    }
}

// 원본 템플릿의 날짜 텍스트박스와 동일한 위치/서식의 도형 XML(앵커)
function coverDateAnchor(dateText) {
    return '<xdr:twoCellAnchor>'
        + '<xdr:from><xdr:col>6</xdr:col><xdr:colOff>224651</xdr:colOff><xdr:row>43</xdr:row><xdr:rowOff>53514</xdr:rowOff></xdr:from>'
        + '<xdr:to><xdr:col>10</xdr:col><xdr:colOff>227000</xdr:colOff><xdr:row>45</xdr:row><xdr:rowOff>78440</xdr:rowOff></xdr:to>'
        + '<xdr:sp macro="" textlink=""><xdr:nvSpPr><xdr:cNvPr id="1001" name="CoverDate"/>'
        + '<xdr:cNvSpPr txBox="1"><a:spLocks noChangeArrowheads="1"/></xdr:cNvSpPr></xdr:nvSpPr>'
        + '<xdr:spPr bwMode="auto"><a:xfrm><a:off x="2853551" y="7463964"/><a:ext cx="1754949" cy="367826"/></a:xfrm>'
        + '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill>'
        + '<a:ln><a:noFill/></a:ln></xdr:spPr>'
        + '<xdr:txBody><a:bodyPr vertOverflow="clip" wrap="square" lIns="91440" tIns="45720" rIns="91440" bIns="45720" anchor="t" upright="1"/>'
        + '<a:lstStyle/><a:p><a:pPr algn="ctr" rtl="0"><a:lnSpc><a:spcPts val="1300"/></a:lnSpc><a:defRPr sz="1000"/></a:pPr>'
        + '<a:r><a:rPr lang="en-US" altLang="ko-KR" sz="1200" b="1" i="0" u="none" strike="noStrike" baseline="0">'
        + '<a:solidFill><a:srgbClr val="000000"/></a:solidFill><a:latin typeface="맑은 고딕"/><a:ea typeface="맑은 고딕"/></a:rPr>'
        + `<a:t>${escapeXml(dateText)}</a:t></a:r></a:p></xdr:txBody></xdr:sp><xdr:clientData/></xdr:twoCellAnchor>`;
}

function escapeXml(s) {
    return String(s).replace(/[<>&'"]/g, (c) => (
        { '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' }[c]
    ));
}

export default exportDbDetailReport;
